<template>
    <div class="tags-editor">
        <div class="tags-editor__tags-input">
            <vue-autosuggest
                v-model="tagKeywords"
                class="tags-editor__autosuggest"
                :suggestions="filteredTagOptions"
                :input-props="{ class: 'tags-editor__input' }"
                :get-suggestion-value="getSuggestionValue"
                @selected="onTagSelect"
            >
                <template slot-scope="{suggestion}">
                    <span class="tags-editor__autosuggest-item">{{ suggestion.item.name }}</span>
                </template>
            </vue-autosuggest>

            <button v-if="createVisible" class="tags-editor__save" @click="saveTag">
                CREATE
            </button>
        </div>
        <ul v-if="value.length" class="tags-editor__tags">
            <li
                v-for="(tag, i) in value"
                :key="tag.id"
                class="tags-editor__tag"
            >
                <button
                    type="button"
                    class="tags-editor__tag-button"
                    @click="() => onTagRemove(i)"
                >
                    {{ tag.name }}

                    <font-awesome-icon class="tags-editor__tag-icon" :icon="['fal', 'times']" />
                </button>
            </li>
        </ul>

        <div v-else class="tags-editor__tagless">
            No tags added
        </div>
    </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import { mapState, mapMutations } from 'vuex';
import { VueAutosuggest } from 'vue-autosuggest';
import taxonomyOptions from '@/graphql/queries/taxonomy-all-tags.gql';
import * as service from '@/sitecore/sitecore-data-service';
import { SHOW_ALERT } from '@/store/types/mutation-types';

export default {
    components: { VueAutosuggest },
    mixins: [clickaway],

    apollo: {
        taxonomyData: {
            query: taxonomyOptions,
            variables() {
                return {
                    rootId: this.root.toLowerCase(),
                };
            },
            update(data) {
                return data.search && data.search.results ? data.search.results.items : [];
            },
            fetchPolicy: 'no-cache',
        },
    },

    props: {
        root: {
            type: String,
            default: () => ('c2cff99cb5e9461996b564ea359f0dcb'),
        },
        createNewEnabled: {
            type: Boolean,
            default: () => (false),
        },
        value: {
            type: Array,
            default: () => ([]),
        },
    },

    data() {
        return {
            tagKeywords: '',
        };
    },

    computed: {
        ...mapState({
            selectedElements: state => state.page.selectedElements,
        }),

        ...mapMutations({
            showAlert: SHOW_ALERT,
        }),

        filteredTagOptions() {
            return [
                {
                    data: this.taxonomyData ? this.taxonomyData.filter((item) => {
                        if (item.id === this.root) return false;
                        if (!this.tagKeywords) return true;
                        return item.name.toLowerCase().includes(this.tagKeywords);
                    }) : {},
                },
            ];
        },
        createVisible() {
            return this.createNewEnabled && this.filteredTagOptions[0] && this.filteredTagOptions[0].data && this.filteredTagOptions[0].data.length === 0;
        },
    },

    methods: {
        getSuggestionValue() {
            return '';
        },

        onTagSelect(tag) {
            if (tag) {
                if (this.value.indexOf(tag.item) === -1) {
                    this.value.push(tag.item);
                }
                this.tagKeywords = '';
                this.$emit('input', this.value);
            }
        },

        onTagRemove(index) {
            this.$emit('delete', index);
        },

        async saveTag() {
            if (this.root && this.tagKeywords) {
                const response = await service.createTaxonomyTag(this.tagKeywords, this.root);

                if (response.status === 'error') {
                    this.showAlert({ description: `Error updating item: ${ response.error }` });
                }

                if (this.value.indexOf({ id: response.data.id, name: response.data.name }) === -1) {
                    this.value.push({ id: response.data.id, name: response.data.name });
                    this.tagKeywords = '';
                    this.$emit('input', this.value);
                    if (this.$apollo.queries.taxonomyData) {
                        await this.$apollo.queries.taxonomyData.refetch();
                    }
                }
            }
        },
    },
};
</script>

<style lang="scss">
@import '@/styles/_includes.scss';

.tags-editor {

    &__autosuggest {
        position: relative;

        .autosuggest__results-container {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            z-index: 1000;
        }

        .autosuggest__results {
            max-height: 14em;
            overflow: auto;
            background-color: var(--form-inputBackgroundColor);
            border: 1px solid var(--form-inputBorderColor);
        }
    }

    &__autosuggest-item {
        display: block;
        font-size: 0.875rem;
        padding: 0.375em 0.5em;
        color: var(--form-inputColor);
        text-transform: initial;

        &:hover {
            cursor: pointer;
            color: var(--form-inputHoverColor);
            background-color: var(--form-inputHoverBackgroundColor);
        }
    }

    &__tags {
        display: flex;
        flex-wrap: wrap;
    }

    &__tag {
        margin-top: 1em;
        margin-right: 1em;
    }

    &__tag-button {
        display: flex;
        align-items: center;
        font-size: 0.75rem;
        padding: 0.375em 0.75rem;
        font-weight: 600;
        color: var(--form-buttonColor);
        background-color: var(--form-buttonBackgroundColor);
        border-radius: 10px;

        &:hover {
            color: var(--form-buttonHoverColor);
            background-color: var(--form-buttonHoverBackgroundColor);
        }
    }

    &__tag-icon {
        font-size: 1.125em;
        margin-left: 0.75em;
    }

    &__tagless {
        font-size: 0.75rem;
        margin-top: 0.5em;
        font-style: italic;
        color: var(--form-controlColor);
    }

    &__tag-error {
        font-size: 0.75rem;
        margin-top: 0.5em;
        font-style: italic;
        color: var(--form-deleteLinkColor);
    }

    &__save {
        display: flex;
        align-items: center;
        font-size: 0.75rem;
        padding: 0.375em 0.75rem;
        font-weight: 600;
        color: var(--form-buttonColor);
        background-color: var(--form-buttonBackgroundColor);
    }

    &__input {
        color: var(--form-inputColor);
        background-color: var(--form-inputBackgroundColor);
        border: 1px solid var(--form-inputBorderColor);
        font-size: 0.875rem;
        width: 100%;
        max-width: 28em;
        padding: 0.5rem 0.75rem;
    }

    &__tags-input {
        display: flex;
        flex-wrap: wrap;
    }
}

</style>
